import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OpenLiftHowItWorks.css";

// Captures réelles du tableau de bord : une version pour chaque langue.
// Fichiers à placer dans public/images/openlift-platform/ :
// parc_fr / parc_en, historique_fr / historique_en, alertes_fr / alertes_en.
// Extensions prises en charge automatiquement : png, webp, jpg, jpeg.
const SLIDES = [
  { id: "parc", imageBase: "parc" },
  { id: "historique", imageBase: "historique" },
  { id: "alertes", imageBase: "alertes" }
];

const IMAGE_EXTENSIONS = ["png", "webp", "jpg", "jpeg"];

// Si une image n'est pas au format PNG, tente les autres formats sans
// modifier les contrôles, la navigation ou le système de traductions.
function tryNextImageFormat(event) {
  const element = event.currentTarget;
  const source = element.getAttribute("src") || "";
  const extensionMatch = source.match(/\.(png|webp|jpg|jpeg)$/i);
  if (!extensionMatch) return;

  const index = IMAGE_EXTENSIONS.indexOf(extensionMatch[1].toLowerCase());
  const nextExtension = IMAGE_EXTENSIONS[index + 1];
  if (nextExtension) {
    element.src = source.replace(/\.(png|webp|jpg|jpeg)$/i, `.${nextExtension}`);
  }
}

// Charge et décode la prochaine capture avant de remplacer celle affichée.
// Le cache évite de recharger une vue lors d'un retour arrière/avant.
const screenshotCache = new Map();

function loadScreenshot(imageName) {
  if (screenshotCache.has(imageName)) return screenshotCache.get(imageName);

  const pending = (async () => {
    for (const extension of IMAGE_EXTENSIONS) {
      const src = `${process.env.PUBLIC_URL}/images/openlift-platform/${imageName}.${extension}`;
      const image = new Image();
      image.decoding = "async";

      const loaded = await new Promise((resolve) => {
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = src;
      });

      if (!loaded) continue;

      // Une erreur de decode() ne doit pas invalider une image qui est chargée.
      if (typeof image.decode === "function") {
        try { await image.decode(); } catch (error) { /* Image déjà chargée. */ }
      }
      return src;
    }
    throw new Error(`Capture indisponible : ${imageName}`);
  })();

  screenshotCache.set(imageName, pending);
  pending.catch(() => screenshotCache.delete(imageName));
  return pending;
}

/**
 * Deux images restent montées dans le DOM : l'ancienne ne disparaît qu'une fois
 * la nouvelle chargée et décodée, puis fondu croisé sans écran vide.
 */
function SmoothScreenshot({ imageName, alt, reduceMotion }) {
  const [layers, setLayers] = useState(() => [
    {
      id: imageName,
      src: `${process.env.PUBLIC_URL}/images/openlift-platform/${imageName}.png`,
      alt
    },
    null
  ]);
  const [visibleSlot, setVisibleSlot] = useState(0);
  const [incomingSlot, setIncomingSlot] = useState(null);
  const [fadeStarted, setFadeStarted] = useState(false);

  const visibleSlotRef = useRef(0);
  const layersRef = useRef(layers);
  const transitioningRef = useRef(false);
  const queuedImageRef = useRef(null);
  const incomingRef = useRef(null);
  const desiredRef = useRef(imageName);
  const finishRef = useRef(null);

  desiredRef.current = imageName;

  const queueTransition = (entry) => {
    if (!entry || entry.id !== desiredRef.current) return;
    if (layersRef.current[visibleSlotRef.current]?.id === entry.id) return;
    if (transitioningRef.current) {
      queuedImageRef.current = entry;
      return;
    }

    const slot = 1 - visibleSlotRef.current;
    const updated = [...layersRef.current];
    updated[slot] = entry;
    layersRef.current = updated;
    incomingRef.current = slot;
    transitioningRef.current = true;
    setLayers(updated);
    setFadeStarted(false);
    setIncomingSlot(slot);
  };

  const finishTransition = (slot) => {
    if (incomingRef.current !== slot) return;
    visibleSlotRef.current = slot;
    incomingRef.current = null;
    transitioningRef.current = false;
    setVisibleSlot(slot);
    setIncomingSlot(null);
    setFadeStarted(false);

    // Si plusieurs navigations se sont produites pendant le fondu, seule
    // la dernière sélection est traitée après la transition en cours.
    const queued = queuedImageRef.current;
    queuedImageRef.current = null;
    if (queued && queued.id === desiredRef.current && queued.id !== layersRef.current[slot]?.id) {
      // La mise à jour est différée d'une frame, après la stabilisation DOM.
      window.requestAnimationFrame(() => {
        if (queued.id === desiredRef.current) queueTransition(queued);
      });
    }
  };
  finishRef.current = finishTransition;

  useEffect(() => {
    let cancelled = false;
    // La première capture reste gérée par l'élément img existant, y compris
    // la recherche des extensions alternatives via onError.
    if (layersRef.current[visibleSlotRef.current]?.id === imageName) {
      // Si un fondu vers une autre vue est encore en cours, revenir à
      // cette sélection dès la fin du fondu (clics rapides / clavier).
      if (transitioningRef.current) {
        queuedImageRef.current = { ...layersRef.current[visibleSlotRef.current], alt };
      }
      return;
    }

    loadScreenshot(imageName)
      .then((src) => {
        if (!cancelled) queueTransition({ id: imageName, src, alt });
      })
      .catch(() => {
        // Conserver la capture précédente si la nouvelle est absente.
      });
    return () => { cancelled = true; };
  }, [imageName, alt]);

  useEffect(() => {
    if (incomingSlot === null) return;
    const index = incomingSlot;
    let firstFrame = 0;
    let secondFrame = 0;

    if (reduceMotion) {
      firstFrame = window.requestAnimationFrame(() => finishRef.current?.(index));
    } else {
      // Deux frames garantissent un état opaque=0 avant la transition CSS.
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => setFadeStarted(true));
      });
    }

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [incomingSlot, reduceMotion]);

  // Préchargement de la capture demandée sans effet de bord sur la navigation.
  return (
    <div className="ol-platform__preview">
      {layers.map((layer, index) => layer && (
        <img
          key={index}
          className={[
            "ol-platform__screenshot",
            index === visibleSlot ? "is-current" : "",
            incomingSlot !== null && index === visibleSlot && fadeStarted ? "is-fading-out" : "",
            incomingSlot === index && fadeStarted ? "is-fading-in" : ""
          ].filter(Boolean).join(" ")}
          src={layer.src}
          alt={layer.alt}
          width="1448"
          height="1086"
          decoding="async"
          loading="eager"
          onError={tryNextImageFormat}
          onTransitionEnd={(event) => {
            if (event.propertyName === "opacity" && incomingSlot === index && fadeStarted) {
              finishRef.current?.(index);
            }
          }}
          aria-hidden={index !== visibleSlot ? "true" : undefined}
        />
      ))}
    </div>
  );
}

const CHANGE_DELAY_MS = 5000;

const CONTENT = {
  fr: {
    eyebrow: "LA PLATEFORME, PAS SEULEMENT UNE PROMESSE",
    titleFirst: "Toute l'information utile,",
    titleLast: "au même endroit.",
    lead: "États, événements, alertes et indicateurs de fonctionnement dans une interface dédiée aux professionnels de l'ascenseur.",
    benefits: [
      "Identifiez les équipements à surveiller",
      "Consultez l'historique des défauts",
      "Suivez les indicateurs disponibles"
    ],
    cta: "Demander une démonstration",
    sectionLabel: "Découvrez les vues de la plateforme OPENlift",
    autoPause: "Mettre le défilement en pause",
    autoPlay: "Reprendre le défilement automatique",
    previous: "Voir la vue précédente",
    next: "Voir la vue suivante",
    navigation: "Choisir la vue du dashboard",
    screens: [
      { label: "Vue parc", title: "Visualisez votre parc", description: "État des équipements et informations essentielles réunis dans une vue commune." },
      { label: "Historique", title: "Retrouvez les événements", description: "Consultez l'historique des événements et défauts pour préparer une analyse." },
      { label: "Alertes", title: "Repérez les situations à suivre", description: "Identifiez les alertes disponibles et aidez vos équipes à définir leurs priorités." }
    ]
  },
  en: {
    eyebrow: "THE PLATFORM, NOT JUST A PROMISE",
    titleFirst: "All the information you need,",
    titleLast: "in one place.",
    lead: "Equipment status, events, alerts and operational indicators in an interface designed for elevator professionals.",
    benefits: [
      "Identify equipment requiring attention",
      "Review fault history",
      "Track available indicators"
    ],
    cta: "Request a demonstration",
    sectionLabel: "Explore the OPENlift platform views",
    autoPause: "Pause automatic slideshow",
    autoPlay: "Resume automatic slideshow",
    previous: "Show previous view",
    next: "Show next view",
    navigation: "Select a dashboard view",
    screens: [
      { label: "Fleet overview", title: "See your fleet clearly", description: "Equipment status and essential information in one shared view." },
      { label: "Event history", title: "Review events", description: "Use event and fault history to prepare an initial diagnosis." },
      { label: "Alerts", title: "Focus on what needs attention", description: "Review available alerts and help your teams set priorities." }
    ]
  }
};

export default function OpenLiftHowItWorks() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0].toLowerCase();
  const copy = CONTENT[lang] || CONTENT.fr;
  // En l'absence d'une traduction EN active, rester sur les captures FR.
  const imageLanguage = lang === "en" ? "en" : "fr";

  const [active, setActive] = useState(0);
  const [pausedByUser, setPausedByUser] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);
  const sectionRef = useRef(null);
  const tabRefs = useRef([]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(query.matches);
    sync();
    if (query.addEventListener) {
      query.addEventListener("change", sync);
      return () => query.removeEventListener("change", sync);
    }
    query.addListener(sync);
    return () => query.removeListener(sync);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined" || !sectionRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      setInView(entry.isIntersecting);
    }, { threshold: 0.15 });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const sync = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", sync);
    sync();
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  const canAutoPlay = !reduceMotion && !pausedByUser && !interacting && inView && pageVisible;

  useEffect(() => {
    if (!canAutoPlay) return;
    const interval = window.setInterval(() => {
      setActive((index) => (index + 1) % SLIDES.length);
    }, CHANGE_DELAY_MS);
    return () => window.clearInterval(interval);
  }, [canAutoPlay]);

  // Charger en avance la vue suivante, sans changer la sélection active.
  // Le fondu attend toujours que l'image demandée soit décodée.
  useEffect(() => {
    if (!inView || !pageVisible) return;
    const nextSlide = SLIDES[(active + 1) % SLIDES.length];
    loadScreenshot(`${nextSlide.imageBase}_${imageLanguage}`).catch(() => {});
  }, [active, imageLanguage, inView, pageVisible]);

  const selectSlide = (index) => setActive((index + SLIDES.length) % SLIDES.length);
  const screen = copy.screens[active];
  const slide = SLIDES[active];
  const screenshotName = `${slide.imageBase}_${imageLanguage}`;

  const onTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % SLIDES.length;
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + SLIDES.length) % SLIDES.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = SLIDES.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    selectSlide(nextIndex);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <section
      id="ol-platform"
      ref={sectionRef}
      className="ol-platform"
      aria-labelledby="ol-how-title"
    >
      <div className="ol-platform__container">
        <div className="ol-platform__topline" aria-hidden="true">
          <span>OPENlift</span><span>LA PLATEFORME</span>
        </div>
        <div className="ol-platform__grid">
          <div className="ol-platform__copy">
            <p className="ol-platform__eyebrow">{copy.eyebrow}</p>
            <h2 className="ol-platform__title" id="ol-how-title">
              {copy.titleFirst}
              <span>{copy.titleLast}</span>
            </h2>
            <p className="ol-platform__lead">{copy.lead}</p>

            <ul className="ol-platform__benefits">
              {copy.benefits.map((value) => (
                <li key={value}>
                  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none">
                    <path d="m3 10 4.5 4.5L17 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{value}</span>
                </li>
              ))}
            </ul>

            <NavLink to="/demo" className="ol-platform__cta">
              {copy.cta}
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavLink>
          </div>

          <div
            className="ol-platform__visual"
            onMouseEnter={() => setInteracting(true)}
            onMouseLeave={() => setInteracting(false)}
            onFocusCapture={() => setInteracting(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false);
            }}
          >
            <div className="ol-platform__frame">
              <div
                id="ol-platform-active-panel"
                role="tabpanel"
                aria-labelledby={`ol-platform-tab-${slide.id}`}
              >
                <SmoothScreenshot
                  imageName={screenshotName}
                  alt={`${screen.label} — ${screen.title}`}
                  reduceMotion={reduceMotion}
                />
              </div>
            </div>

            <div className="ol-platform__view-details">
              <div>
                <p className="ol-platform__view-number">0{active + 1} / 03</p>
                <h3 className="ol-platform__view-title">{screen.title}</h3>
                <p className="ol-platform__view-description">{screen.description}</p>
              </div>
            </div>

            <div className="ol-platform__controls">
              <button
                type="button"
                className="ol-platform__arrow"
                onClick={() => selectSlide(active - 1)}
                aria-label={copy.previous}
              >
                <span aria-hidden="true">←</span>
              </button>
              <div className="ol-platform__tabs" role="tablist" aria-label={copy.navigation}>
                {SLIDES.map((item, index) => (
                  <button
                    type="button"
                    role="tab"
                    key={item.id}
                    id={`ol-platform-tab-${item.id}`}
                    aria-controls="ol-platform-active-panel"
                    aria-selected={active === index}
                    tabIndex={active === index ? 0 : -1}
                    ref={(el) => { tabRefs.current[index] = el; }}
                    className={`ol-platform__tab${active === index ? " is-active" : ""}`}
                    onClick={() => selectSlide(index)}
                    onKeyDown={(event) => onTabKeyDown(event, index)}
                  >
                    <span>0{index + 1}</span>
                    {copy.screens[index].label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="ol-platform__arrow"
                onClick={() => selectSlide(active + 1)}
                aria-label={copy.next}
              >
                <span aria-hidden="true">→</span>
              </button>
              {!reduceMotion && (
                <button
                  type="button"
                  className="ol-platform__pause"
                  onClick={() => setPausedByUser((current) => !current)}
                  aria-label={pausedByUser ? copy.autoPlay : copy.autoPause}
                  aria-pressed={pausedByUser}
                >
                  <span aria-hidden="true">{pausedByUser ? "▶" : "Ⅱ"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
